/* eslint-disable @typescript-eslint/no-explicit-any */
import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios"
import { setData } from "../app/slices/patients/patientSlice"
import { sendError } from "./httpMedit"

//const SHAPE_BASE_URL = 'https://staging-identity.3shape.com/connect/authorize?client_id=Ulab.Staging&response_type=code&scope=openid+api+offline_access+communicate.connections.manage+data.companies.read_only+data.users.read_only&redirect_uri=http://qa.udesign.cloud/callback3shape&code_challenge=m91uAguiEsBSYGo4bvk7VFUs42cZeaNDu1Y7N8-T2PE&code_challenge_method=S256&response_mode=query'
// Client credential
// ID : PkpuC42YZcl2O5gJoaMEkG5owIOaKnMo
// PW : EKjf3oIfyfyoSttGo4a017V1qsMsoe2p

const { VITE_DOMAIN } = import.meta.env

const SHAPE_DATA_URL = "https://ammetadata.3shapecommunicate.com"

class Http3Shape {
  private instance: AxiosInstance

  constructor() {
    this.instance = axios.create({
      timeout: 60000, // 设置超时时间，单位为毫秒
      // 可以在这里设置其他默认配置，如 headers
      //withCredentials: true,
    })
  }

  async get<T = any>(
    path: string,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> {
    try {
      const r = await this.updateTokenIfExpired()
      if(!r) {
        console.error("update 3shape token error")
        return
      }

      let jsonObj = JSON.parse(localStorage.getItem("3shapeData"))

      const response = await this.instance.get<T>(SHAPE_DATA_URL + path, {
        ...config,
        headers: {
          ...(config && config.headers),
          Authorization: `Bearer ${jsonObj.accessToken}`,
        },
      })
      return response
    } catch (error) {
      // 在这里处理错误，例如抛出自定义错误或返回默认值
      console.error("GET request failed:", error)
      throw error
    }
  }

  async post<T = any>(
    path: string, // 设置默认请求地址
    data: string,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> {
    try {
      // 手动构建x-www-form-urlencoded格式字符串
      const formData = Object.entries(data)
        .map(([key, value]) => {
          return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
        })
        .join("&")

      const response = await this.instance.post(
        SHAPE_DATA_URL + path,
        formData,
        {
          ...config,
          headers: {
            ...(config && config.headers),
            "Content-Type": "application/x-www-form-urlencoded",
          },
        },
      )
      return response
    } catch (error) {
      sendError()
      console.error("POST request failed:", error)
      throw error
    }
  }

  async postWithFullPath<T = any>(
    path: string, // 设置默认请求地址
    data: string,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> {
    try {
      // 手动构建x-www-form-urlencoded格式字符串
      const formData = Object.entries(data)
        .map(([key, value]) => {
          return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
        })
        .join("&")

      const response = await this.instance.post(path, formData, {
        ...config,
        headers: {
          ...(config && config.headers),
          "Content-Type": "application/x-www-form-urlencoded",
        },
      })
      return response
    } catch (error) {
      sendError()
      console.error("POST request failed:", error)
      throw error
    }
  }

  async postLogin<T = any>(
    path: string, // 设置默认请求地址
    data: string,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> {
    try {
      // 手动构建x-www-form-urlencoded格式字符串
      const formData = Object.entries(data)
        .map(([key, value]) => {
          return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
        })
        .join("&")

      const response = await this.instance.post(path, formData, {
        ...config,
        headers: {
          ...(config && config.headers),
          "Content-Type": "application/x-www-form-urlencoded",
        },
      })
      return response
    } catch (error) {
      console.error("POST request failed:", error)
      throw error
    }
  }

  async downloadBlob(stlUrl: string): Promise<Blob | null> {
    this.updateTokenIfExpired()
    let jsonObj = JSON.parse(localStorage.getItem("3shapeData"))
    try {
      const response = await fetch(stlUrl, {
        method: "GET", // 通常下载文件是 GET 请求
        headers: {
          Authorization: `Bearer ${jsonObj.accessToken}`, // <-- 这里添加 Bearer token
          // 'Content-Type': 'application/json', // 如果你的API要求，通常下载文件不需要
          // 'Accept': 'model/stl, application/octet-stream', // 可选：指定接受的MIME类型
        },
        // 如果需要发送 cookies 或处理 CORS 凭据，可以使用 credentials
        // credentials: 'same-origin' // 'include' 或 'omit'
      })

      // 检查响应状态码
      if (!response.ok) {
        // 如果响应不成功（例如 401 Unauthorized, 404 Not Found, 500 Internal Server Error）
        const errorText = await response.text() // 尝试获取错误信息
        console.error(
          `Failed to download blob file: ${response.status} ${response.statusText}`,
          errorText,
        )
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      // 获取 Blob 对象，适合文件下载
      const stlBlob = await response.blob()

      // 可选：在这里处理文件下载，例如创建 Blob URL 并触发下载
      // const blobUrl = URL.createObjectURL(stlBlob);
      // const a = document.createElement('a');
      // a.href = blobUrl;
      // a.download = 'test_down_file.'+ stlBlob.type; // 设置下载文件名
      // document.body.appendChild(a);
      // a.click();
      // document.body.removeChild(a);
      // URL.revokeObjectURL(blobUrl); // 释放 Blob URL 资源

      return stlBlob
    } catch (error) {
      console.error("Error during download:", error)
      return null
    }
  }

  async getHTMLContent(htmlUrl: string): Promise<string | null> {
    this.updateTokenIfExpired()
    let jsonObj = JSON.parse(localStorage.getItem("3shapeData"))
    try {
      const response = await fetch(htmlUrl, {
        method: "GET", // 通常下载文件是 GET 请求
        headers: {
          Authorization: `Bearer ${jsonObj.accessToken}`, // <-- 这里添加 Bearer token
          // 'Content-Type': 'application/json', // 如果你的API要求，通常下载文件不需要
          // 'Accept': 'model/stl, application/octet-stream', // 可选：指定接受的MIME类型
        },
        // 如果需要发送 cookies 或处理 CORS 凭据，可以使用 credentials
        // credentials: 'same-origin' // 'include' 或 'omit'
      })

      // 检查响应状态码
      if (!response.ok) {
        // 如果响应不成功（例如 401 Unauthorized, 404 Not Found, 500 Internal Server Error）
        const errorText = await response.text() // 尝试获取错误信息
        console.error(
          `Failed to download blob file: ${response.status} ${response.statusText}`,
          errorText,
        )
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const htmlContent = await response.text()
      return htmlContent
    } catch (error) {
      console.error("Error getting html", error)
      return null
    }
  }

  async updateTokenIfExpired() {
    //console.log("token_data(3shape)=", localStorage.getItem("3shapeData"))
    let jsonObj = JSON.parse(localStorage.getItem("3shapeData"))

    const currentSeconds: number = Math.floor(new Date().getTime() / 1000)

    if (currentSeconds - jsonObj.lastTokenTime >= jsonObj.expireSecs) {
      console.log(
        "need refresh token , passed secs=",
        currentSeconds - jsonObj.lastTokenTime,
      )
      const r = await refreshToken()
      return r
    }
    return true
  }

// end of class definition
}
// eslint-disable-next-line import/no-anonymous-default-export
export default new Http3Shape()

export async function get3ShapeToken(code_: string) {
  const shape3Client = new Http3Shape() // 设置基础URL

  try {
    const postResponse = await shape3Client.postLogin(
      "https://identity.3shape.com/connect/token",
      {
        code: code_,
        client_id: "UlUTXMHM7GGRTVb",
        grant_type: "authorization_code",
        redirect_uri: `${VITE_DOMAIN}/settings/integrations`,
        code_verifier: "a2EPFknotUTgGV0esB2XRuGBZqd0si64tVhjOeuuLGs",
      },
    )
    console.log("get first token:", postResponse.data.access_token)

    const currentSeconds: number = Math.floor(new Date().getTime() / 1000)
    console.log("cur Unix timestamp:", currentSeconds)
    // 判断是否已有scanner被链接上了
    const meditScanner = localStorage.getItem("meditData")
    const alliesScanner = localStorage.getItem("alliesData")
    localStorage.setItem(
      "3shapeData",
      JSON.stringify({
        accessToken: postResponse.data.access_token,
        refreshToken: postResponse.data.refresh_token,
        expireSecs: postResponse.data.expires_in,
        lastTokenTime: currentSeconds,
        isPrimary: !meditScanner && !alliesScanner ? true : false,
      }),
    )
    return true
  } catch (error) {
    // 处理请求错误
    console.error("Request error:", error)
  }
}

// any get and post call this before actual request .
// first we need to get refreshToken from server, if not found , means client should login once .
// after login and get token , save into localStorage and use it .
// if get refreshtoken from server OK , then use refresh token to get accesstoken and save new refreshtoken to server .

export async function getEffectToken() {}

export async function refreshToken() {
  const shape3Client = new Http3Shape() // 设置基础URL

  try {
    let jsonObj = JSON.parse(localStorage.getItem("3shapeData"))
    console.log("3shape refreshToken=", jsonObj.refreshToken)

    const getResponse = await shape3Client.postWithFullPath(
      "https://identity.3shape.com/connect/token",
      {
        client_id: "UlUTXMHM7GGRTVb",
        grant_type: "refresh_token",
        refresh_token: jsonObj.refreshToken,
      },
    )
    console.log("GET response data:", getResponse.data)
    
    // if (getResponse.status != 200) {
    //   sendError()
    //   return false
    // }

    const currentSeconds: number = Math.floor(new Date().getTime() / 1000)
    console.log("cur Unix timestamp:", currentSeconds)
    const shapeObj = JSON.parse(localStorage.getItem("3shapeData"))
    localStorage.setItem(
      "3shapeData",
      JSON.stringify({
        accessToken: getResponse.data.access_token,
        refreshToken: getResponse.data.refresh_token,
        expireSecs: getResponse.data.expires_in,
        lastTokenTime: currentSeconds,
        isPrimary: shapeObj.isPrimary,
      }),
    )

    return true
  } catch (error) {
    // 处理请求错误
    console.error("Request error:", error)
    return false
  }
}

interface DataItem {
  Attachments: any[]
  Scans: any[]
  Info: {
    CaseId: string
    PatientName: string
    CreateTime: string
    FirstName: string
    LastName: string
  } //Record<string, unknown>;
}

export async function get3ShapeCaselist(
  page: string = "",
  fromDate: string = "",
  toDate: string = "",
) {
  const shape3Client = new Http3Shape() // 设置基础URL
  let caselist: any[] = []
  let caseCount: number = 0

  try {
    if (page == "") {
      const getResponse = await shape3Client.get(`/api/cases`)
      caselist = getResponse.data.Cases
      caseCount = getResponse.data.Count
    } else {
      if (fromDate == "") {
        const getResponse = await shape3Client.get(`/api/cases?page=${page}`)
        caselist = getResponse.data.Cases
        caseCount = getResponse.data.Count
      } else if (toDate == "") {
        const getResponse = await shape3Client.get(
          `/api/cases?page=${page}&from=${fromDate}`,
        )
        caselist = getResponse.data.Cases
        caseCount = getResponse.data.Count
      } else {
        const getResponse = await shape3Client.get(
          `/api/cases?page=${page}&from=${fromDate}&to=${toDate}`,
        )
        caselist = getResponse.data.Cases
        caseCount = getResponse.data.Count
      }
    }

    console.log("caselist==========", caselist)
    // arragen data into specified stucture and back to UI

    // return thedata.map((item: any) => ({
    //   // 根据实际 API 返回结构调整以下字段
    //   Attachments: item.Attachments || [],
    //   Scans: item.Scans || [],
    //   Info: {
    //     CaseId: item.Id,   // 示例字段
    //     PatientName: item.PatientName,
    //     CreateTime: item.Created,
    //     LastName:item.Patient.LastName,
    //     FirstName:item.Patient.FirstName
    //   },
    // }));

    const result: DataItem[] = caselist.map((item: any) => {
      return {
        Attachments: item.Attachments || [],
        Scans: item.Scans || [],
        Info: {
          CaseId: item.Id, // 示例字段
          PatientName: item.PatientName,
          CreateTime: item.Created,
          LastName: item.Patient.LastName,
          FirstName: item.Patient.FirstName,
          Comments: item.Comments || [],
        },
      }
    })

    console.log("result== ", result)
    return { caseCount, result }
  } catch (error) {
    // 处理请求错误
    console.error("Request error:", error)
  }
}

// function stringToArrayBuffer(str: string): ArrayBuffer {
//   const buf = new ArrayBuffer(str.length)
//   const bufView = new Uint8Array(buf)
//   for (let i = 0; i < str.length; i++) {
//     bufView[i] = str.charCodeAt(i)
//   }
//   return buf
// }

// function saveBlobToFile(blob: Blob, fileName: string) {
//   const url = URL.createObjectURL(blob)
//   const a = document.createElement("a")
//   a.href = url
//   a.download = fileName // 设置下载文件名
//   document.body.appendChild(a)
//   a.click() // 触发下载
//   document.body.removeChild(a) // 移除元素
//   URL.revokeObjectURL(url) // 释放内存
// }

export async function downloadScan(
  path?: string,
  name?: string,
  fileType?: string,
  dispatch?: any,
) {
  // const response1 = await fetch(upStl)
  // const response2 = await fetch(lowStl)
  // const blob1 = await response1.blob()
  // const blob2 = await response2.blob()
  // return {
  //   upper: new File([blob1], "arch_u.stl", { type: "arch_u.stl" }),
  //   lower: new File([blob2], "arch_l.stl", { type: "arch_l.stl" }),
  // }

  const shape3Client = new Http3Shape() // 设置基础URL

  try {
    dispatch && dispatch(setData({ loading: "pending" }))

    const blob = await shape3Client.downloadBlob(`${path}`)
    console.log(blob)

    const file = new File([blob], name, {
      type: fileType || blob.type || "application/octet-stream",
    })

    dispatch && dispatch(setData({ loading: "succeeded" }))
    return file
  } catch (error) {
    console.error("Request error:", error)
    dispatch && dispatch(setData({ loading: "failed" }))
    return null
  }
}

export async function get3ShapeCaseById(
  id: string = "ba60bba5-3dd0-4d5f-aff7-62d006e7b3c7",
) {
  const shape3Client = new Http3Shape() // 设置基础URL

  // const encodedId = encodeURIComponent(id);
  // console.log('Debug: Type of id:', typeof id);
  // console.log('Debug: Value of id:', id);
  try {
    const getResponse = await shape3Client.get(`/api/cases/${id}`)
    console.log("GET response data:", getResponse.data)
  } catch (error) {
    // 处理请求错误
    console.error("Request error:", error)
  }
}

export async function getCaseVersion(
  id: string = "ba60bba5-3dd0-4d5f-aff7-62d006e7b3c7",
  version: string = "",
) {
  const shape3Client = new Http3Shape() // 设置基础URL

  const encodedId = encodeURIComponent(id)
  const encodedversion = encodeURIComponent(version)

  try {
    const getResponse = await shape3Client.get(
      `/api/cases/${encodedId}/version/${encodedversion}`,
    )
    console.log("GET response data:", getResponse.data)
  } catch (error) {
    // 处理请求错误
    console.error("Request error:", error)
  }
}

export async function search(
  searchStr: string = "",
  page: string,
  fromDate: string = "",
  toDate: string = "",
  version: string = "",
) {
  const shape3Client = new Http3Shape() // 设置基础URL
  const encodedsearchStr = encodeURIComponent(searchStr)
  const encodedversion = encodeURIComponent(version)

  let caselist: any[] = []
  let caseCount: number = 0
  try {
    if (page == "") {
      const getResponse = await shape3Client.get(
        `/api/cases/search?searchString=${encodedsearchStr}&page=0`,
      )
      caselist = getResponse.data.Cases
      caseCount = getResponse.data.Count
    } else {
      if (fromDate == "") {
        const getResponse = await shape3Client.get(
          `/api/cases/search?searchString=${encodedsearchStr}&page=${page}`,
        )
        caselist = getResponse.data.Cases
        caseCount = getResponse.data.Count
      } else if (toDate == "") {
        const getResponse = await shape3Client.get(
          `/api/cases/search?searchString=${encodedsearchStr}&page=${page}&from=${fromDate}`,
        )
        caselist = getResponse.data.Cases
        caseCount = getResponse.data.Count
      } else {
        const getResponse = await shape3Client.get(
          `/api/cases/search?searchString=${encodedsearchStr}&page=${page}&from=${fromDate}&to=${toDate}`,
        )
        caselist = getResponse.data.Cases
        caseCount = getResponse.data.Count
      }
    }

    console.log("searched case list :", caselist)
    const result: DataItem[] = caselist.map((item: any) => {
      return {
        Attachments: item.Attachments || [],
        Scans: item.Scans || [],
        Info: {
          CaseId: item.Id, // 示例字段
          PatientName: item.PatientName,
          CreateTime: item.Created,
          LastName: item.Patient.LastName,
          FirstName: item.Patient.FirstName,
          Comments: item.Comments || [],
        },
      }
    })
    return { caseCount, result }
  } catch (error) {
    // 处理请求错误
    console.error("Request error:", error)
  }
}

export async function getCaseComment(
  id: string = "ba60bba5-3dd0-4d5f-aff7-62d006e7b3c7",
  commentId: string = "",
) {
  // return "a string with case comment text"

  const shape3Client = new Http3Shape() // 设置基础URL

  const encodedId = encodeURIComponent(id)
  const encodedcommentId = encodeURIComponent(commentId)

  try {
    let jsonObj = JSON.parse(localStorage.getItem("3shapeData"))
    // console.log("token=", localStorage.getItem("3shapeData"))
    const getResponse = await shape3Client.get(
      `/api/cases/${encodedId}/comments/${encodedcommentId}`,
      {
        headers: {
          Authorization: `Bearer ${jsonObj.accessToken}`,
          "Custom-Header": "custom_value",
        },
      },
    )
    console.log("GET response data:", getResponse.data)
    return getResponse.data
  } catch (error) {
    // 处理请求错误
    console.error("Request error:", error)
  }
}

interface DateParts {
  year: string
  month: string
  day: string
}

export async function getBirthday(url: string): Promise<DateParts> {
  const shape3Client = new Http3Shape() // 设置基础URL
  // 创建解析器将HTML字符串转换为DOM
  const html = await shape3Client.getHTMLContent(url)
  const parser = new DOMParser()
  const doc = parser.parseFromString(html, "text/html")

  // 查找所有包含文本"Patient birthday:"的单元格
  const labelCells = doc.querySelectorAll("td")

  for (let i = 0; i < labelCells.length; i++) {
    const cell = labelCells[i]
    // 检查单元格文本内容
    if (cell.textContent?.trim() === "Patient birthday:") {
      // 获取下一个兄弟元素（包含实际日期的单元格）
      const nextCell = cell.nextElementSibling

      if (nextCell && nextCell.classList.contains("underline")) {
        const dateValue = nextCell.textContent?.trim()

        if (dateValue) {
          // 查找 '-' 的位置
          const dashIndex = dateValue.indexOf("-")
          // 查找 '/' 的位置
          const slashIndex = dateValue.indexOf("/")

          let separator: string | null = null
          let separatorIndex: number = -1

          // 判断哪个分隔符先出现
          if (dashIndex !== -1 && slashIndex !== -1) {
            // 两个都存在，选择靠前的那个
            if (dashIndex < slashIndex) {
              separator = "-"
              separatorIndex = dashIndex
            } else {
              separator = "/"
              separatorIndex = slashIndex
            }
          } else if (dashIndex !== -1) {
            // 只有 '-' 存在
            separator = "-"
            separatorIndex = dashIndex
          } else if (slashIndex !== -1) {
            // 只有 '/' 存在
            separator = "/"
            separatorIndex = slashIndex
          }
          const [month, day, year] = dateValue.split(separator)
          return {
            year: year,
            month: month,
            day: day,
          }
        }
      }
    }
  }
  return {
    year: "",
    month: "",
    day: "",
  }
}
